import { useEffect, useMemo } from "react";

import LoadingSpinner from "components/atoms/LoadingSpinner/LoadingSpinner";
import { Table } from "components/molecules/Table";
import { getDisplayInfo } from "utils/formatting";

import useQueryBuildings from "ApiLayer/Building/queryBuildings";
import useQueryProjects from "ApiLayer/Project/queryProjects";
import errorFallbackUI from "components/molecules/ErrorFallbackUI.tsx/ErrorFallbackUI";
import { ComponentErrorBoundary } from "components/molecules/ErrorStates";
import posthog from "posthog-js";
import { useNavigate } from "react-router-dom";
import { falseProjects, TABLE_IDS } from "utils/constants";
import { formatProjectsData } from "utils/helpers";
import "./MeasuresProjectsReport.scss";

interface MeasuresProjectsReportProps {
    setExportData: (data: any) => void;
    filtersetUid: string;
    reportedVsPublished: string;
}

function MeasuresProjectsReport({ setExportData, filtersetUid, reportedVsPublished }: MeasuresProjectsReportProps) {
    // Hooks
    const navigate = useNavigate();
    const { data: buildingsResponse, isLoading: buildingsLoading } = useQueryBuildings(
        filtersetUid,
        reportedVsPublished
    );
    const { data: projectsResponse, isLoading: projectsLoading } = useQueryProjects(filtersetUid, reportedVsPublished);

    const formattedData = formatProjectsData(buildingsResponse, projectsResponse);

    const excludedKeys = ["buildingArchetype", "stateProvince", "tags", "carbonReductionMeasureUid"];
    const tableColumns = useMemo(() => {
        if (!formattedData || formattedData.length === 0) return null;
        const formattedDataKeys = Object.keys(formattedData[0]).filter((key) => !excludedKeys.includes(key));

        return formattedDataKeys.map((key) => {
            const typedKey = key as keyof MeasuresProjectsReportTableRow;
            if (typeof formattedData[0][typedKey] === "string") {
                const { formatFunction, humanReadable, tooltip } = getDisplayInfo(key);

                return {
                    render: formatFunction,
                    key,
                    title: humanReadable,
                    tooltip,
                };
            }

            const columnValues = formattedData.map((row) => row[typedKey]) as number[];

            const { formatFunction, humanReadable, unit, tooltip } = getDisplayInfo(key, columnValues);
            return {
                render: formatFunction,
                key,
                title: `${humanReadable} ${unit && `(${unit})`}`,
                tooltip,
            };
        });
    }, [formattedData]);

    const dataSource = useMemo(() => {
        if (!formattedData) return null;

        const filteredData = formattedData.filter((row) => !falseProjects.includes(row.carbonReductionMeasureType));

        return filteredData.map((row, index) => ({
            ...row,
            key: `${row.carbonReductionMeasureType ? row.carbonReductionMeasureType : "defaultKey"}-${index}`,
        }));
    }, [formattedData]);

    const exportData = dataSource?.map((row) => ({
        ...row,
    }));

    useEffect(() => {
        // Using preventive state update to avoid a rerender that bugs pagination
        if (exportData) {
            setExportData((prev: any) => {
                if (JSON.stringify(prev) !== JSON.stringify(exportData)) {
                    return exportData;
                }
                return prev;
            });
        }
    }, [exportData]);
    if (buildingsLoading || projectsLoading) {
        return <LoadingSpinner />;
    }

    if (!dataSource || !tableColumns) return null;

    const handleProjectRowClick = (row: MeasuresProjectsReportTableRow) => {
        const { buildingModelUid: uid, carbonReductionMeasureType, carbonReductionMeasureUid } = row;

        posthog.capture("projectsRowClick", { carbonReductionMeasureType });

        if (uid) navigate(`/building/${uid}/measures/${carbonReductionMeasureUid}`);
    };

    return (
        <div className="measures-projects-report">
            <ComponentErrorBoundary fallback={errorFallbackUI("tableError")} originComponent="MeasuresProjectsReport">
                <Table
                    dataSource={dataSource}
                    columns={tableColumns}
                    className="projects-report-table"
                    pagination={true}
                    onRowClick={(row) => handleProjectRowClick(row)}
                    parentComponentWithSearchBarId={TABLE_IDS.MEASURES_PROJECTS}
                    defaultOrderBy={{ key: "yearApplied", order: "asc" }}
                    getRowHref={(row) => `/building/${row.buildingModelUid}/measures/${row.carbonReductionMeasureUid}`}
                />
            </ComponentErrorBoundary>
        </div>
    );
}

export default MeasuresProjectsReport;
